// Route: /progress — обзор прогресса обучения «коуч».
// KPI-жетоны → графики успеваемости + heatmap ритма → прогресс по материалам →
// лента активности + адаптивные рекомендации. Тактильный язык «Картотека».
// URL state: ?range=week|month|year, ?demo=... (preview UI-состояний).
// Реальный backend: tRPC api.progress.* / api.recommendations.* (_api.ts).
// См. docs/ui-spec/progress.{desktop,mobile}.md.

import { tolerantSearch } from "@studyhub/shared/utils/tolerant-search";
import { LIST_PAGE_SHELL } from "@studyhub/ui";
import { Button } from "@studyhub/ui/components/button";
import { EmptyState } from "@studyhub/ui/components/empty-state";
import { Skeleton } from "@studyhub/ui/components/skeleton";
import { SectionHeader } from "@studyhub/ui/components/studyhub/section-header";
import { IconChartLine, IconUpload } from "@tabler/icons-react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { lazy, Suspense, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import {
	type ProgressRange,
	useActivityFeed,
	useModuleProgress,
	useProgressHeatmap,
	useProgressOverview,
	useProgressSnapshots,
} from "@/components/progress/_api";
import { ActivityFeed } from "@/components/progress/activity-feed";
import { RhythmHeatmap } from "@/components/progress/heatmap/rhythm-heatmap";
import { KpiStrip } from "@/components/progress/kpi-strip";
import { ModuleProgress } from "@/components/progress/module-progress";
import { TimeRangePicker } from "@/components/progress/time-range-picker";
import { RecommendationsWidget } from "@/components/recommendations/recommendations-widget";
import { OfflineDetailState } from "@/components/shared/offline-detail-state";
import { PagePendingFallback } from "@/components/shared/page-pending-fallback";
import {
	SectionIntro,
	SectionIntroReopen,
	useSectionIntro,
} from "@/components/shared/section-intro";
import { StreakModal } from "@/components/streaks/streak-modal";
import { isOfflineWithoutCache } from "@/lib/offline-query-state";
import { useIsLgUp } from "@/lib/platform-detect";

// Recharts — тяжёлая зависимость, ленивая загрузка.
const MasteryChart = lazy(() =>
	import("@/components/progress/mastery-chart").then((m) => ({ default: m.MasteryChart })),
);
const TimeChart = lazy(() =>
	import("@/components/progress/time-chart").then((m) => ({ default: m.TimeChart })),
);

/** Скелетон-карта графика (Suspense fallback) — повторяет тактильную панель e1. */
function ChartFallback(): React.ReactElement {
	return (
		<div className="rounded-[var(--radius-card)] border border-border bg-card p-4 shadow-e1">
			<Skeleton className="mb-3 h-8 w-44" />
			<Skeleton className="h-52 w-full rounded-[var(--radius-tile)] lg:h-64" />
		</div>
	);
}

const searchSchema = z.object({
	range: z.enum(["week", "month", "year"]).optional(),
	demo: z
		.enum(["default", "loading", "empty", "error", "streak-active", "streak-broken", "milestone"])
		.optional(),
});

export const Route = createFileRoute("/progress")({
	validateSearch: tolerantSearch(searchSchema),
	component: ProgressPage,
	pendingComponent: PagePendingFallback,
});

function ProgressPage(): React.ReactElement {
	const { t } = useTranslation("progress");
	const intro = useSectionIntro("progress");
	const isDesktop = useIsLgUp();
	const search = Route.useSearch();
	const navigate = useNavigate({ from: Route.fullPath });
	const range: ProgressRange = search.range ?? "week";

	const demo = search.demo;
	const forceLoading = demo === "loading";
	const forceError = demo === "error";
	const forceEmpty = demo === "empty";

	const overviewDemo = forceError ? "error" : forceEmpty ? "empty" : undefined;
	const streakDemo =
		demo === "streak-broken" || demo === "milestone" || demo === "streak-active" ? demo : undefined;

	const overviewQuery = useProgressOverview({ demo: overviewDemo });
	const snapshotsQuery = useProgressSnapshots({ range, demo: overviewDemo });
	const modulesQuery = useModuleProgress({ demo: overviewDemo });
	const feedQuery = useActivityFeed({ demo: overviewDemo });
	// Heatmap имеет собственный диапазон — не зависит от range-фильтра графиков.
	const heatmapDays = isDesktop ? 98 : 35;
	const heatmapQuery = useProgressHeatmap(heatmapDays);

	const [streakOpen, setStreakOpen] = useState(false);

	const setRange = (next: ProgressRange): void => {
		void navigate({
			search: (prev) => ({ ...prev, range: next === "week" ? undefined : next }),
			replace: true,
		});
	};

	const isLoading = forceLoading || overviewQuery.isLoading;
	// Пусто только при полном отсутствии учебной активности. Раньше гейт смотрел
	// на totalMaterials (= число строк mastery_by_topic), который у реальных
	// пользователей пуст до первого пересчёта mastery — экран ошибочно показывал
	// «загрузите материал» даже после пройденных тестов и повторений карточек.
	// Теперь — по реальным KPI overview (карточки/тесты/минуты), которые читают
	// живые таблицы напрямую.
	const overview = overviewQuery.data;
	// Статистика считается на сервере: офлайн запрос приостановлен и данных нет.
	// Без этой ветки экран показывал онбординговое «у вас пока нет активности»
	// пользователю с сотнями повторений — то есть врал о потере данных.
	const isOffline = isOfflineWithoutCache(overviewQuery);
	const isEmpty =
		!isLoading &&
		!isOffline &&
		(overview == null || (overview.completedActivities === 0 && overview.totalStudyMinutes === 0));

	// Авто-открытие streak-модалки в demo=milestone.
	useEffect(() => {
		if (demo === "milestone") setStreakOpen(true);
	}, [demo]);

	return (
		<>
			<div className={LIST_PAGE_SHELL}>
				{/* Заголовок страницы. stackActions — на <sm пикер периодов уезжает
				    под заголовок отдельной строкой (на всю ширину), а не ужимает title. */}
				<SectionHeader
					icon={<IconChartLine stroke={1.75} />}
					category="mastery"
					title={t("title")}
					description={t("subtitle")}
					stackActions
					actions={
						<>
							<SectionIntroReopen intro={intro} />
							<TimeRangePicker value={range} onChange={setRange} className="max-sm:w-full" />
						</>
					}
				/>

				{/* Баннер-объяснение раздела: текст из манифеста модуля, состояние
				    «свёрнут» — серверное (см. section-intro.tsx). */}
				<SectionIntro intro={intro} />

				{isOffline && <OfflineDetailState onRetry={() => void overviewQuery.refetch()} />}

				{/* Пустое состояние — молодой аккаунт */}
				{isEmpty && (
					<EmptyState
						icon={<IconChartLine stroke={1.5} />}
						title={t("empty.title")}
						description={t("empty.body")}
						action={
							<Button asChild className="cursor-pointer">
								<Link to="/materials" preload="intent">
									<IconUpload size={16} />
									{t("empty.upload")}
								</Link>
							</Button>
						}
					/>
				)}

				{!isEmpty && !isOffline && (
					<>
						{/* KPI-жетоны */}
						<KpiStrip
							data={overviewQuery.data}
							isLoading={isLoading}
							range={range}
							onStreakClick={() => setStreakOpen(true)}
						/>

						{/* Графики успеваемости + heatmap ритма */}
						<section className="grid grid-cols-1 gap-4 lg:grid-cols-3">
							<Suspense fallback={<ChartFallback />}>
								<MasteryChart
									data={snapshotsQuery.data?.items}
									isLoading={snapshotsQuery.isLoading}
								/>
							</Suspense>
							<Suspense fallback={<ChartFallback />}>
								<TimeChart data={snapshotsQuery.data?.items} isLoading={snapshotsQuery.isLoading} />
							</Suspense>
							<RhythmHeatmap
								data={heatmapQuery.data}
								isLoading={heatmapQuery.isLoading}
								columns={isDesktop ? 14 : 7}
							/>
						</section>

						{/* Прогресс по материалам */}
						<ModuleProgress data={modulesQuery.data?.items} isLoading={modulesQuery.isLoading} />

						{/* Лента активности + адаптивные рекомендации */}
						{isDesktop ? (
							<section className="grid grid-cols-1 gap-4 lg:grid-cols-3">
								<div className="lg:col-span-2">
									<ActivityFeed data={feedQuery.data?.items} isLoading={feedQuery.isLoading} />
								</div>
								<div className="lg:col-span-1">
									<RecommendationsWidget
										demo={demo === "empty" ? "empty" : undefined}
										initialLimit={5}
									/>
								</div>
							</section>
						) : (
							// На mobile рекомендации и лента — две одинаковые тактильные
							// панели e2 со своими внутренними заголовками (без внешнего
							// дубль-SectionHeader). Симметрия с ActivityFeed.
							<div className="space-y-6">
								<RecommendationsWidget
									demo={demo === "empty" ? "empty" : undefined}
									initialLimit={4}
								/>
								<ActivityFeed data={feedQuery.data?.items} isLoading={feedQuery.isLoading} />
							</div>
						)}
					</>
				)}
			</div>

			<StreakModal open={streakOpen} onClose={() => setStreakOpen(false)} demo={streakDemo} />
		</>
	);
}
